import {
    PocButton,
    PocComponentFooter,
    PocComponentHeader,
    PocProgressSpinner,
} from 'commons/components/components-wrapper';
import { Size } from '@one/web-components';
import { useTranslation } from 'core/i18n/i18n';
import React from 'react';
import { PopupProps } from 'react-popup-manager';
import { ConditionalForm } from '../form-elements';
import { ModalDialogLayout } from './modal-dialog.style';
import { useModalAutoFocus } from './use-modal-autofocus.hook';

export interface ModalDialogProps extends React.PropsWithChildren, PopupProps {
    title?: string | React.ReactNode;
    className?: string;
    headerOptions?: React.ReactNode;
    footerOptions?: React.ReactNode;
    prefixFooter?: React.ReactNode;
    loading?: boolean;
    isForm?: boolean;
    showFooter?: boolean;
    size?: Exclude<Size, 'xSmall' | 'xLarge'>;
    cancelButtonText?: string;
    cancelButtonDisabled?: boolean;
    onlyCloseAction?: boolean;
    disableEscape?: boolean;
    initialDelay?: number;
}

export const ModalDialog: React.FC<ModalDialogProps> = (props) => {
    const {
        title,
        children,
        headerOptions,
        footerOptions,
        prefixFooter,
        loading = false,
        isForm = false,
        showFooter = true,
        disableEscape = true,
        isOpen,
        className,
        size = 'small',
        cancelButtonText,
        cancelButtonDisabled = false,
        onlyCloseAction = false,
        initialDelay,
        onClose,
    } = props;
    const { t } = useTranslation();
    const ref = useModalAutoFocus({ loading, initialDelay });

    return (
        <ModalDialogLayout
            ref={ref}
            disableBackdropClick
            disableScrollLock
            disableEscapeKeydown={disableEscape}
            noAutoFocus
            visible={isOpen}
            size={size}
            className={className}
        >
            {title ? (
                <PocComponentHeader slot="header" separator>
                    {title}
                    {headerOptions ? <div slot="suffix">{headerOptions}</div> : undefined}
                </PocComponentHeader>
            ) : undefined}

            <ConditionalForm condition={isForm}>
                <div className="modal-dialog-content">{loading ? <PocProgressSpinner /> : children}</div>
                {showFooter ? (
                    <PocComponentFooter slot="footer" separator data-testid="modal-dialog-footer">
                        <div slot="prefix" className={loading ? 'loading' : ''}>
                            {prefixFooter ?? undefined}
                        </div>
                        <div slot="suffix">
                            <PocButton
                                variant={onlyCloseAction ? 'primary' : 'secondary'}
                                size="medium"
                                onClick={onClose}
                                disabled={cancelButtonDisabled}
                            >
                                {cancelButtonText ?? t('commons.components.modal.modal-dialog.footer.button.cancel')}
                            </PocButton>
                            {!onlyCloseAction && footerOptions && (
                                <div className={loading ? 'loading' : ''}>{footerOptions}</div>
                            )}
                        </div>
                    </PocComponentFooter>
                ) : undefined}
            </ConditionalForm>
        </ModalDialogLayout>
    );
};
